import { ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { SharedService } from '../../shared/services/shared.service';
import { EventEntry, Form4473, Note } from '../../shared/models/general';
import { Subscription } from 'rxjs';
import { format } from 'date-fns';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';
import { CardTemplateComponent } from 'src/app/shared/components/card-template/card-template.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatStepperModule } from '@angular/material/stepper';
import {
  buildEventRecord,
  createDateFromComponents,
  extractTrailingNumbers,
  findUserDigitalSignature,
  getDay,
  getMonth,
  getYear,
  isToday,
} from '../../shared/functions';
import {
  Answers,
  DateFormats,
  Events,
  Questions,
  signatureType,
  State,
} from '../../shared/models/constants';

@Component({
  selector: 'app-incomplete-dispositions',
  standalone: true,
  imports: [FormsModule, DatePipe, NgClass, CardTemplateComponent, MatProgressBarModule, MatStepperModule],
  templateUrl: './incomplete-dispositions.component.html',
  styleUrl: './incomplete-dispositions.component.scss',
})
export class IncompleteDispositionsComponent {
  private destroyRef = inject(DestroyRef);
  subscriptions = new Subscription();
  drafts: any[] = [];
  filteredDrafts: any[] = [];
  searchTerm: string = '';
  incomplete_forms: Form4473[] = [];
  selectedDraftId: string | null = null;

  isDeletingDraft = false;
  step_labels = {
    firearms: 'Firearms',
    buyer_4473: "Buyer's 4473",
    seller_4473: 'Seller 4473',
    background_check: 'Background Check',
    buyer_recert: 'Buyer Recertification',
    review_4473: 'Review 4473 PDF',
    seller_certify_4473: 'Certify & Record 4473',
    //  record_4473: 'Record 4473',
  };
  stepConfigs = [
    { label: this.step_labels.firearms, fields: {} },
    { label: this.step_labels.buyer_4473, fields: {} },
    { label: this.step_labels.seller_4473, fields: {} },
    { label: this.step_labels.background_check, fields: {} },
    { label: this.step_labels.buyer_recert, fields: {} },
    { label: this.step_labels.review_4473, fields: {} },
    { label: this.step_labels.seller_certify_4473, fields: {} },
    // { label: this.step_labels.record_4473, fields: {} },
  ];
  constructor(
    public sharedService: SharedService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    // signatureCaptured$
    this.subscriptions.add(
      this.sharedService.signatureCaptured$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((signature) => {
          console.warn('Signature captured:', signature);
          if (
            signature.signature_type ===
            signatureType.find((s) => s.name === 'buyer_recert').name
          ) {
            this.buyerRecertSignatureCaptured(signature.signature);
          } else {
            this.sellerCertificationSignatureCaptured(signature.signature);
          }
        }),
    );
    // pdfOverlayClosed$
    this.subscriptions.add(
      this.sharedService.pdfOverlayClosed$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.markDraftAsReviewed();
        }),
    );
    // yes_no_submit
    this.subscriptions.add(
      this.sharedService.yes_no_submit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.delete_draft
          ) {
            this.deleteDraft(response['optional_details']['draft_id']);
          }
          if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.generate_form_3310
          ) {
            this.sharedService.generate3310AndOpenEmailModal(response['optional_details']['completed_form4473']);
          }
          if (
            response['answer'] === Answers.no &&
            response['question'] === Questions.generate_form_3310
          ) {
            this.sharedService.createPending3310Record(response['optional_details']['completed_form4473']);
          }
        }),
    );
  }

  async ngOnInit() {
    // Ensure company info is loaded
    const companyId = this.sharedService.company_info?.company_id;

    if (!companyId) {
      console.error('Company ID not available');
      this.sharedService.raiseErrorToast(
        'Company information not loaded. Please try again.',
      );
      // Optionally redirect or retry loading company info
      return;
    }

    try {
      this.incomplete_forms =
        await this.sharedService.getIncompleteForm4473ByCompanyId(companyId);

      // Ensure we use the latest locally saved data for the pending draft
      // to avoid race conditions if the server update is delayed/queued.
      if (this.sharedService.pending_4473?._id) {
        const idx = this.incomplete_forms.findIndex(
          (f) => f._id === this.sharedService.pending_4473._id,
        );
        if (idx !== -1) {
          this.incomplete_forms[idx] = this.sharedService.pending_4473;
        } else {
          this.incomplete_forms.push(this.sharedService.pending_4473);
        }
      }
    } catch (error) {
      console.error('Error loading incomplete forms:', error);
    }

    // Generate fresh drafts from server data
    this.drafts = this.createDrafts();

    this.drafts = this.drafts.filter(
      (draft: any) => !this.isDraftComplete(draft),
    );
    this.filteredDrafts = [...this.drafts];
    if (this.sharedService.pending_4473) {
      this.selectedDraftId = this.sharedService.pending_4473._id;
    }
    this.cdr.markForCheck();
  }

  private createDrafts(): any[] {
    return this.incomplete_forms.map((form) => {
      let activeStep = 0;
      let seller_4473_created = false;
      let background_check_complete = false;
      let buyer_recertification_complete = false;
      let pdf_reviewed_signed = false;
      let seller_certified = false;

      // Step 0: Firearms complete
      const hasCompleteFirearms =
        form.firearms?.length > 0 && form.firearms.every((f) => f.firearm_id);
      if (hasCompleteFirearms) activeStep = 1;

      // Step 1: Transferee complete
      const hasCompleteTransferee = !!form.transferee?.name_first;
      if (hasCompleteTransferee) activeStep = 2;

      // Step 2: Transferor (seller 4473) complete
      const hasCompleteTransferor = !!form.transferor?.id_number;
      if (hasCompleteTransferor) {
        activeStep = 3;
        seller_4473_created = true;
      }

      // Step 3: Background Check complete
      const hasValidPermit = form.transferor?.no_nics_valid_permit;

      const hasNicsWithInitialProceed =
        !!form.transferor?.nics_transaction_number &&
        (form.transferor?.nics_initial_response === 'Proceed' ||
          form.transferor?.nics_initial_response === '1');

      const hasNicsWithPriorProceed =
        !!form.transferor?.nics_transaction_number &&
        (form.transferor?.nics_secondary_response_type_prior_to_transfer ===
          'Proceed' ||
          form.transferor?.nics_secondary_response_type_prior_to_transfer ===
            '1');

      const hasNicsWithPostProceed =
        !!form.transferor?.nics_transaction_number &&
        (form.transferor?.nics_secondary_response_type_post_transfer ===
          'Proceed' ||
          form.transferor?.nics_secondary_response_type_post_transfer === '1');

      const hasCompleteBackground =
        hasValidPermit ||
        hasNicsWithInitialProceed ||
        hasNicsWithPriorProceed ||
        hasNicsWithPostProceed;

      if (hasCompleteBackground) {
        activeStep = 4;
        background_check_complete = true;
      }

      // Step 4: Buyer Recertification
      const needsRecert = this.needsRecertification(form);
      const hasRecert = this.hasRecertification(form);

      if (hasCompleteBackground) {
        if (!needsRecert) {
          activeStep = 5; // Move to Review PDF
          buyer_recertification_complete = true;
        } else {
          // Recertification needed
          activeStep = 4; // Move to Buyer Recertification
          buyer_recertification_complete = false;
        }
      }

      // Step 5: PDF reviewed/signed (seller signature indicates PDF review done)
      const sellerSignaturePresent = !!form.transferor?.transferor_signature;
      const hasPdfReviewed = sellerSignaturePresent;
      // If recert is needed: require buyer_recertification_complete to advance.
      // If recert is not needed: allow advancing once seller signature exists.
      if (sellerSignaturePresent) {
        //(hasPdfReviewed && (buyer_recertification_complete || !needsRecert)) ||
        activeStep = 6; // Move to Seller Certify
        pdf_reviewed_signed = true;
      }

      // Step 6: Seller certified (same signature field used here)
      const hasSellerCertified = sellerSignaturePresent && pdf_reviewed_signed;
      if (hasSellerCertified) {
        activeStep = 7; // Move to Record step
        seller_certified = true;
      }

      return {
        id: form._id || uuidv4(),
        timestamp: form.createdAt || new Date().toISOString(),
        data: {
          form4473_id: form._id,
          transaction_serial_number: form.transaction_serial_number,
          reserved_firearms: (form.firearms || []).map((firearm) => ({
            firearm_id: firearm.firearm_id?.toString() || '',
            firearm: `${firearm.manufacture || ''} ${
              firearm.model || ''
            }`.trim(),
            serial_number: firearm.serial_number || '',
            firearm_type: firearm.firearm_type || '',
            caliber_gauge: firearm.caliber_gauge || '',
          })),
          transferee_4473: {
            name_first: form.transferee?.name_first || '',
            name_middle: form.transferee?.name_middle || '',
            name_last: form.transferee?.name_last || '',
            birth_month: form.transferee?.birth_month || '',
            birth_day: form.transferee?.birth_day || '',
            birth_year: form.transferee?.birth_year || '',
            transferee_cert_month: form.transferee?.transferee_cert_month || '',
            transferee_cert_day: form.transferee?.transferee_cert_day || '',
            transferee_cert_year: form.transferee?.transferee_cert_year || '',
            transferee_cert_signature:
              form.transferee?.transferee_cert_signature || '',
            transferee_recert_signature:
              form.transferee?.transferee_recert_signature || '',
            transferee_recert_day: form.transferee?.transferee_recert_day || '',
            transferee_recert_month:
              form.transferee?.transferee_recert_month || '',
            transferee_recert_year:
              form.transferee?.transferee_recert_year || '',
          },
          drivers_license: {
            nameFirst: form.transferee?.name_first || '',
            nameLast: form.transferee?.name_last || '',
            licenseNumber: form.transferor?.id_number || '',
          },
          seller_4473_created: seller_4473_created,
          background_check_complete: background_check_complete,
          buyer_recertification_complete: buyer_recertification_complete,
          needs_recertification: needsRecert,
          pdf_reviewed_signed: pdf_reviewed_signed,
          seller_certified: seller_certified,
        },
        activeStep: activeStep,
      };
    });
  }

  getCompletionPercentage(draft: any): number {
    const validSteps = this.stepConfigs.filter((_, i) =>
      this.isStepCompleteInDraft(draft, i),
    ).length;
    return Math.round((validSteps / this.stepConfigs.length) * 100);
  }

  isStepCompleteInDraft(draft: any, index: number): boolean {
    const needsRecert = draft.data?.needs_recertification;
    switch (index) {
      case 0:
        return draft.data?.reserved_firearms?.length > 0;
      case 1:
        return (
          draft.data?.transferee_4473?.transferee_cert_signature?.length > 0
        );
      case 2:
        return !!draft.data?.seller_4473_created;
      case 3:
        return !!draft.data?.background_check_complete;
      case 4: {
        // Buyer Recertification
        if (!needsRecert) {
          return true;
        }
        return !!draft.data?.buyer_recertification_complete;
      }
      case 5:
        return !!draft.data?.pdf_reviewed_signed;
      case 6:
        return !!draft.data?.seller_certified;
      default:
        return false;
    }
  }

  isDraftComplete(draft: any): boolean {
    // A draft is complete when ALL steps are complete
    const totalSteps = this.stepConfigs.length; // Should be 6 steps
    for (let i = 0; i < totalSteps; i++) {
      if (!this.isStepCompleteInDraft(draft, i)) {
        return false; // If any step is incomplete, draft is incomplete
      }
    }
    return true; // All steps are complete
  }

  ensureDraftSelected(draftId: string) {
    if (this.selectedDraftId !== draftId) {
      this.selectDraft(draftId);
    }
  }

  onStepperClick(event: MouseEvent, draft: any) {
    this.ensureDraftSelected(draft.id);

    // Check if the click occurred on a step header
    const target = event.target as HTMLElement;
    const header = target.closest('.mat-step-header');

    if (header) {
      const indexAttr = header.getAttribute('aria-posinset');
      if (indexAttr) {
        // aria-posinset is 1-based index
        const clickedIndex = parseInt(indexAttr, 10) - 1;

        // If clicking on the already active step, selectionChange won't fire.
        // We manually trigger the resume logic here.
        if (clickedIndex === draft.activeStep) {
          this.resumeDraft(draft);
        }
      }
    }
  }

  onStepChange(event: any, draft: any) {
    this.ensureDraftSelected(draft.id);
    this.selectStep(draft.id, event.selectedIndex);
  }

  selectStep(draftId: string, stepIndex: number) {
    if (this.selectedDraftId !== draftId) {
      this.selectDraft(draftId);
    }
    const draft = this.drafts.find((d) => d.id === draftId);

    // Step 0 (Firearms) and Step 1 (Buyer's 4473) are generally not editable here or have specific constraints
    if (stepIndex <= 1) {
      this.sharedService.raiseWarningToast('This step can not be edited');
      return;
    }

    // Prevent accessing Certify & Record (Step 6) if previous steps are incomplete
    if (stepIndex === 6 && draft && draft.activeStep < 6) {
      this.sharedService.raiseWarningToast(
        'Please complete all previous steps before certifying.',
      );
      return;
    }
    if (draft) {
      // Temporarily set activeStep to the clicked step so resumeDraft picks it up
      // OR directly call the logic from resumeDraft here.
      // Let's defer to resumeDraft logic by faking the activeStep or passing it.
      const originalStep = draft.activeStep;
      draft.activeStep = stepIndex;
      this.resumeDraft(draft);
      draft.activeStep = originalStep; // Restore if needed, though resumeDraft might navigate away
    }
  }

  async resumeDraft(draft: any) {
    // Select this draft to show only this card
    this.selectDraft(draft.id);

    // Load the Form4473 data into shared service first
    if (draft.data?.form4473_id) {
      this.sharedService.pending_4473 = this.incomplete_forms.find(
        (f) => f._id === draft.data.form4473_id,
      );
    }

    // REMOVED THIS LINE:
    // this.selectDraft(null);

    // Use the activeStep already calculated when creating the draft
    const step = draft.activeStep || 0;

    // Navigate based on active step
    switch (step) {
      case 0:
        // Navigate to firearm selection
        this.sharedService.changeRoute('firearm-selection');
        break;
      case 1:
        // Navigate to buyer form
        this.sharedService.changeRoute('buyer-form-4473');
        break;
      case 2:
        // Navigate to seller form
        this.sharedService.changeRoute('seller-form-4473');
        break;
      case 3:
        // Background check step - navigate to seller form (or dedicated background check route)
        this.sharedService.changeRoute('seller-form-4473');
        break;
      case 4:
        // Buyer recertification - open digital signature modal
        this.sharedService.openDigitalSignature(
          signatureType.find((s) => s.name === 'buyer_recert').name,
        );
        break;
      case 5:
        // Review PDF step
        console.warn('Generating and displaying 4473 PDF');
        try {
          await this.sharedService.generateAndDisplay4473(
            this.sharedService.pending_4473,
          );
        } catch (error) {
          console.error('Error:', error);
        }
        break;
      case 6:
        this.saveCompletedDraft();
    }
  }

  deleteDraftClick(id: string) {
    this.sharedService.openYesNoModal(Questions.delete_draft, { draft_id: id });
  }
  async deleteDraft(id: string) {
    if (this.isDeletingDraft) {
      console.warn('Delete already in progress');
      return;
    }

    this.isDeletingDraft = true;

    try {
      const draft = this.drafts.find((d) => d.id === id);

      if (!draft) {
        console.error('Draft not found');
        return;
      }
      // delete the all reserved things associted with the draft
      // delete firarm reservation
      if (draft.data?.reserved_firearms) {
        // Always refresh reserved list first so deletions use current server data.
        await this.sharedService.getReservedFirearmList();

        for (const firearm of draft.data.reserved_firearms) {
          // reservation_id is not stored in draft data; delete all matching reservations.
          const matchingReservations =
            this.sharedService.reserved_firearm_list.filter(
              (rf) =>
                String(rf.firearm_id) === String(firearm.firearm_id) &&
                (!firearm.serial_number ||
                  rf.serial_number === firearm.serial_number),
            );

          if (matchingReservations.length === 0) {
            console.warn(
              'No reservation found for firearm_id:',
              firearm.firearm_id,
            );
          } else {
            for (const reservation of matchingReservations) {
              await this.sharedService.deleteFirearmFromReserveList(
                reservation.reservation_id,
              );
            }
          }

          const bound_book_entry = this.sharedService.bound_book.find(
            (entry) => String(entry.firearm_id) === String(firearm.firearm_id),
          );
          if (bound_book_entry) {
            bound_book_entry.state = State.acquisition;
            await this.sharedService.updateBoundBookEntry(bound_book_entry);
          }
        }
      }
      // Delete from database
      await this.sharedService.deleteForm4473(id);

      // Remove from memory
      this.drafts = this.drafts.filter((d) => d.id !== id);

      // Update filtered drafts
      this.filterDrafts();

      // Clear selection
      if (this.selectedDraftId === id) {
        this.selectedDraftId = null;
      }

      this.sharedService.raiseSuccessToast('Draft successfully deleted');
    } catch (error) {
      console.error('Error deleting draft:', error);
      this.sharedService.raiseErrorToast('Failed to delete draft');
    } finally {
      // ✅ Reset flag after operation completes
      this.isDeletingDraft = false;
    }
  }

  openHome() {
    this.sharedService.changeRoute('home');
  }

  filterDrafts() {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.filteredDrafts = this.drafts;
      return;
    }
    this.filteredDrafts = this.drafts.filter((draft) => {
      const first = draft.data?.drivers_license?.nameFirst?.toLowerCase() || '';
      const last = draft.data?.drivers_license?.nameLast?.toLowerCase() || '';
      return first.includes(term) || last.includes(term);
    });
  }

  buyerRecertSignatureCaptured(buyer_recert_signature: string) {
    const today = new Date();
    this.sharedService.pending_4473.transferee.transferee_recert_signature =
      buyer_recert_signature;
    this.sharedService.pending_4473.transferee.transferee_recert_day =
      getDay(today);
    this.sharedService.pending_4473.transferee.transferee_recert_month =
      getMonth(today);
    this.sharedService.pending_4473.transferee.transferee_recert_year =
      getYear(today);
    this.updateDraftAfterCertification(true);
  }

  sellerCertificationSignatureCaptured(seller_certification_signature: string) {
    const today = new Date();
    this.sharedService.pending_4473.transferor.transferor_signature =
      seller_certification_signature;
    this.sharedService.pending_4473.transferor.transferor_certification_signature_day =
      getDay(today);
    this.sharedService.pending_4473.transferor.transferor_certification_signature_month =
      getMonth(today);
    this.sharedService.pending_4473.transferor.transferor_certification_signature_year =
      getYear(today);
    this.updateDraftAfterCertification(true);
  }
  deselectDraft() {
    this.selectedDraftId = null;
    this.filteredDrafts = this.drafts;
    this.filterDrafts();
  }
  selectDraft(draftId: string | null) {
    console.warn(
      'Selected draft:',
      draftId,
      'selectedDraftId:',
      this.selectedDraftId,
    );
    if (draftId) {
      this.selectedDraftId = draftId;
      this.filteredDrafts = this.drafts.filter(
        (d) => d.id === this.selectedDraftId,
      );
    }
  }

  isSelected(draftId: string): boolean {
    return this.selectedDraftId === draftId;
  }

  shouldShowDraft(draft: any): boolean {
    // If no draft is selected, show all
    if (!this.selectedDraftId) return true;
    // If a draft is selected, only show that one
    return draft.id === this.selectedDraftId;
  }
  // Helper method to check if recertification is needed
  private needsRecertification(form: Form4473): boolean {
    if (form.transferor.transferor_signature) {
      return false;
    }
    const recert_date = createDateFromComponents(
      form.transferee?.transferee_recert_month,
      form.transferee?.transferee_recert_day,
      form.transferee?.transferee_recert_year,
    );
    const cert_date = createDateFromComponents(
      form.transferee?.transferee_cert_month,
      form.transferee?.transferee_cert_day,
      form.transferee?.transferee_cert_year,
    );
    const today = isToday();
    if (cert_date.getTime() === today.getTime()) {
      return false; // cert date is today - no recertification needed
    } else if (recert_date.getTime() === today.getTime()) {
      return false; // recert date is today - no recertification needed
    } else if (
      cert_date.getTime() !== today.getTime() &&
      (recert_date === null || isNaN(recert_date.getTime()))
    ) {
      return true; // cert date is in the past - recertification needed
    } else {
      return true;
    }
  }

  // Helper method to check if recertification is complete
  private hasRecertification(form: Form4473): boolean {
    const recertMonth = form.transferee?.transferee_recert_month;
    const recertDay = form.transferee?.transferee_recert_day;
    const recertYear = form.transferee?.transferee_recert_year;

    if (!recertMonth || !recertDay || !recertYear) {
      return false;
    } else {
      return true;
    }
  }

  private async updateDraftAfterCertification(update?: boolean) {
    try {
      // Update the Form4473 in the database
      if (update) {
        if (!!this.sharedService.pending_4473.transaction_serial_number) {
          await this.createTransactionSerialNumber();
          await this.sharedService.updateForm4473(
            this.sharedService.pending_4473,
          );
        } else {
          await this.sharedService.updateForm4473(
            this.sharedService.pending_4473,
          );
        }
      }

      // Find the draft in memory and update it
      const draftIndex = this.drafts.findIndex(
        (d: any) => d.data.form4473_id === this.sharedService.pending_4473._id,
      );

      if (draftIndex !== -1) {
        // Update the draft with new recertification data
        this.drafts[
          draftIndex
        ].data.transferee_4473.transferee_recert_signature =
          this.sharedService.pending_4473.transferee.transferee_recert_signature;
        this.drafts[draftIndex].data.transferee_4473.transferee_recert_day =
          this.sharedService.pending_4473.transferee.transferee_recert_day;
        this.drafts[draftIndex].data.transferee_4473.transferee_recert_month =
          this.sharedService.pending_4473.transferee.transferee_recert_month;
        this.drafts[draftIndex].data.transferee_4473.transferee_recert_year =
          this.sharedService.pending_4473.transferee.transferee_recert_year;

        // Mark recertification as complete
        this.drafts[draftIndex].data.buyer_recertification_complete = true;

        // Advance to next step (Review PDF)
        this.drafts[draftIndex].activeStep = 5;

        // Update component state
        this.drafts = this.drafts.filter(
          (draft: any) => !this.isDraftComplete(draft),
        );
        this.filteredDrafts = this.drafts;

        this.sharedService.raiseSuccessToast('Buyer recertification completed');

        // Navigate to next step (Review PDF)
        this.sharedService.generateAndDisplay4473(
          this.sharedService.pending_4473,
        );
      } else {
        console.error('Draft not found in localStorage');
        this.sharedService.raiseErrorToast('Failed to update draft');
      }
    } catch (error) {
      console.error('Error updating draft after recertification:', error);
      this.sharedService.raiseErrorToast('Failed to save recertification');
    }
  }
  // Add this new method to mark the draft as reviewed:
  private async markDraftAsReviewed() {
    try {
      // Update the Form4473 in the database if needed
      // if (this.sharedService.pending_4473) {
      //   await this.sharedService.updateForm4473(
      //     this.sharedService.pending_4473
      //   );
      // }

      // Find the draft in memory and update it
      const draftIndex = this.drafts.findIndex(
        (d: any) => d.data.form4473_id === this.sharedService.pending_4473?._id,
      );

      if (draftIndex !== -1) {
        // Mark PDF as reviewed/signed
        this.drafts[draftIndex].data.pdf_reviewed_signed = true;

        // Advance to next step (Seller Certify)
        this.drafts[draftIndex].activeStep = 6;

        // Preserve the selected draft ID before filtering
        const currentSelectedId = this.selectedDraftId;

        // Update component state WITHOUT creating new array references
        const updatedDrafts = this.drafts.filter(
          (draft: any) => !this.isDraftComplete(draft),
        );

        // Only update if different to avoid unnecessary change detection
        if (JSON.stringify(this.drafts) !== JSON.stringify(updatedDrafts)) {
          this.drafts = updatedDrafts;
          this.filteredDrafts = updatedDrafts;
        }

        // ALWAYS restore selection - don't clear it
        this.selectedDraftId = currentSelectedId;

        console.log('Draft marked as reviewed, ready for seller certification');
      } else {
        console.error('Draft not found in localStorage');
        this.sharedService.raiseErrorToast('Failed to update draft');
      }
    } catch (error) {
      console.error('Error marking draft as reviewed:', error);
      this.sharedService.raiseErrorToast('Failed to save review status');
    }
  }
  async review4473(draft: any) {
    // Select this draft (which will filter the list)
    this.selectDraft(draft.id);

    // Load the Form4473 data into shared service first
    if (draft.data?.form4473_id) {
      this.sharedService.pending_4473 = this.incomplete_forms.find(
        (f) => f._id === draft.data.form4473_id,
      );
    }
    await this.sharedService.generateAndDisplay4473(
      this.sharedService.pending_4473,
    );
  }
  async saveCompletedDraft() {
    const digital_signature = findUserDigitalSignature(
      this.sharedService,
      this.sharedService.logged_in_user.user_id,
    );
    if (digital_signature) {
      this.sharedService.pending_4473.transferor.ffl_employee_completing_nics =
        this.sharedService.logged_in_user.name_first +
        ' ' +
        this.sharedService.logged_in_user.name_last;
      this.sharedService.pending_4473.transferor.transferor_name =
        this.sharedService.logged_in_user.name_first +
        ' ' +
        this.sharedService.logged_in_user.name_last;
      this.sharedService.pending_4473.transferor.transferor_signature =
        digital_signature;
      this.sharedService.pending_4473.transferor.transferor_certification_signature_day =
        getDay(new Date());
      this.sharedService.pending_4473.transferor.transferor_certification_signature_month =
        getMonth(new Date());
      this.sharedService.pending_4473.transferor.transferor_certification_signature_year =
        getYear(new Date());
      try {
        await this.createTransactionSerialNumber();
        await this.sharedService.updateForm4473(
          this.sharedService.pending_4473,
        );
        await this.updateDisposedBoundBookProperties();
        this.sharedService.raiseSuccessToast(
          'Form 4473 recorded successfully.',
        );
        // remove the completed draft and unfilter all other drafts
        // 1. Update the draft in memory to be complete
        const draftIndex = this.drafts.findIndex(
          (d: any) =>
            d.data.form4473_id === this.sharedService.pending_4473._id,
        );

        if (draftIndex !== -1) {
          this.drafts[draftIndex].data.seller_certified = true;
          // Mark this step complete so isDraftComplete returns true
          this.drafts[draftIndex].activeStep = 7;
        }

        // 2. Remove from component's drafts list (since it's now complete)
        // Re-filter the primary list
        this.drafts = this.drafts.filter(
          (draft: any) => !this.isDraftComplete(draft),
        );
        // delete buyers divers license is found in this.sharedService.recent_drivers_license
        if (
          this.sharedService.pending_4473.transferor.id_type_of_id ==
          'Driver License'
        ) {
          const drivers_license =
            this.sharedService.recent_drivers_license.find(
              (d: any) =>
                d.id_number ===
                this.sharedService.pending_4473.transferor.id_number,
            );
          this.sharedService.deleteDriverLicense(drivers_license);
        }
        // 3. Reset selection and show all incomplete drafts
        this.selectedDraftId = null;
        this.filteredDrafts = this.drafts;
        // Save reference before clearing so the yes/no handler can use it
        const completedForm = this.sharedService.pending_4473;
        this.sharedService.pending_4473 = null; // Clear pending
        this.filterDrafts(); // Apply any existing search filter

        // Prompt to generate Form 3310.4 if 2 or more handguns were transferred
        const handgunCount = (completedForm?.firearms || []).filter((fw: any) => {
          const raw = (fw?.firearm_type ?? fw?.type ?? '').toString().toLowerCase();
          return raw.includes('pistol') || raw.includes('revolver') || raw.includes('handgun');
        }).length;
        if (handgunCount >= 2) {
          this.sharedService.openYesNoModal(Questions.generate_form_3310, { completed_form4473: completedForm });
        }
      } catch (error) {
        console.error('Error saving completed draft:', error);
        this.sharedService.raiseErrorToast('Failed to save review status');
      }
    } else {
      this.sharedService.openDigitalSignature(
        signatureType.find((s) => s.name === 'ffl_employee').name,
      );
    }
  }
  async createTransactionSerialNumber() {
    const maxTransactionSerialNumber =
      await this.sharedService.findMaxTransactionSerialNumber();
    let trailingNumbers = extractTrailingNumbers(maxTransactionSerialNumber);
    this.sharedService.pending_4473.transaction_serial_number = (
      Number(trailingNumbers) + 1
    ).toString();
  }
  async refresh() {
    const companyId = this.sharedService.company_info?.company_id;
    try {
      this.incomplete_forms =
        await this.sharedService.getIncompleteForm4473ByCompanyId(companyId);

      // Ensure we use the latest locally saved data for the pending draft
      // to avoid race conditions if the server update is delayed/queued.
      if (this.sharedService.pending_4473?._id) {
        const idx = this.incomplete_forms.findIndex(
          (f) => f._id === this.sharedService.pending_4473._id,
        );
        if (idx !== -1) {
          this.incomplete_forms[idx] = this.sharedService.pending_4473;
        } else {
          this.incomplete_forms.push(this.sharedService.pending_4473);
        }
      }
      console.warn('Incomplete forms loaded:', this.incomplete_forms);
    } catch (error) {
      console.error('Error loading incomplete forms:', error);
    }

    // Generate fresh drafts from server data
    this.drafts = this.createDrafts();

    this.drafts = this.drafts.filter(
      (draft: any) => !this.isDraftComplete(draft),
    );
    this.filteredDrafts = this.drafts;
    if (this.sharedService.pending_4473) {
      this.selectedDraftId = this.sharedService.pending_4473._id;
      console.warn('Auto-selecting pending draft:', this.selectedDraftId);
    }
    this.cdr.markForCheck();
  }
  async updateDisposedBoundBookProperties() {
    const updated_datetime = format(new Date(), DateFormats.M_D_Y_H_M_S);
    for (const firearm of this.sharedService.pending_4473.firearms) {
      // console.warn('getting the corresponding bound book entry for the firearm: ', firearm.firearm_id);
      const bound_book_entry =
        await this.sharedService.getBoundBookEntryByFirearmId(
          firearm.firearm_id,
        );
      //console.warn('bound book entry found: ', bound_book_entry);
      bound_book_entry.d_date_disposed = updated_datetime;
      bound_book_entry.d_form4473_id =
        this.sharedService.pending_4473.transaction_serial_number;
      bound_book_entry.d_first_name =
        this.sharedService.pending_4473.transferee.name_first;
      bound_book_entry.d_last_name =
        this.sharedService.pending_4473.transferee.name_last;
      bound_book_entry.d_middle_name =
        this.sharedService.pending_4473.transferee.name_middle;
      bound_book_entry.d_sr_name =
        this.sharedService.pending_4473.transferee.cadence;
      bound_book_entry.d_address =
        this.sharedService.pending_4473.transferee.address_street;
      bound_book_entry.d_address2 = null;
      bound_book_entry.d_zip_code =
        this.sharedService.pending_4473.transferee.address_zip_code;
      bound_book_entry.d_state =
        this.sharedService.pending_4473.transferee.address_state;
      bound_book_entry.d_city =
        this.sharedService.pending_4473.transferee.address_city;
      bound_book_entry.state = State.disposition;
      bound_book_entry.notes.push(this.createDispositionNote(updated_datetime));
      bound_book_entry.event_log.push(
        buildEventRecord(this.sharedService, Events.create_disposition),
      );
      bound_book_entry.d_form4473_id =
        this.sharedService.pending_4473.transaction_serial_number;
      // console.warn('Updating bound book entry: ', bound_book_entry);
      await this.sharedService.updateBoundBookEntry(bound_book_entry);
      //  console.warn('Bound book entry updated: ', bound_book_entry);
    }
  }
  createDispositionNote(event_datetime: string): Note {
    let note: Note = new Note();
    note.note_type = 'Disposition';
    ((note.username = this.sharedService.logged_in_user.username),
      (note.comment = State.disposition),
      (note.dateTime = format(event_datetime, DateFormats.M_D_Y_H_M_S)));
    return note;
  }

  showdraft(draft) {
    console.log(draft);
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
